/**
 * Every read the client detail makes, as `queryOptions`, with its keys exported.
 *
 * The keys descend from `entities/client`'s `clientRoot`, so a write anywhere in the panel that
 * genuinely invalidates "everything about clients" still does it with one prefix — and so the two
 * surfaces cannot end up with two unrelated cache trees for the same client.
 *
 * ── Only the visible tab reads ────────────────────────────────────────────────────────────────
 * Mobile mounts one pane at a time and this app does the same, by construction rather than by a
 * flag: each tab's hook lives in the component the tab renders, so a tab that is not mounted has
 * no query. The summary column's four reads are the exception — they are always on screen.
 *
 * ── `from`/`to` are computed here, once ───────────────────────────────────────────────────────
 * The attendance card folds one thirty-day window and the read asks for exactly that.
 * `model/attendance` folds whatever it is handed and takes no dates, so the window is a property
 * of the REQUEST and belongs here; putting it in the model would make the card's arithmetic depend
 * on a number the model cannot see.
 */

import { queryOptions, type PlaceholderDataFunction } from '@tanstack/react-query';

import { clientDetailRoot } from '~/entities/client';
import {
  getCustomerIdBody,
  getCustomerIdCompliance,
  getCustomerIdConsistency,
  getCustomerIdExerciseMatrix,
  getCustomerIdMuscleVolume,
  getCustomerIdPersonalRecords,
  getCustomerIdProgressSummary,
  getCustomerIdRecentSessions,
  getCustomerIdTrainedExercises,
  getCustomerIdTrainingTrends,
} from '~/shared/api/generated/endpoints/customer/customer';
import { getBookingPolicyCustomerCustomerIdSelfBooking } from '~/shared/api/generated/endpoints/booking-policy/booking-policy';
import {
  getNutritionCustomersCustomerIdCalculatorInputs,
  getNutritionCustomersCustomerIdDay,
  getNutritionCustomersCustomerIdRange,
  getNutritionCustomersCustomerIdTarget,
} from '~/shared/api/generated/endpoints/nutrition/nutrition';
import { isGone } from '~/shared/api/mutator';
import { BOOKING_ROOT } from '~/features/credit-ledger';

/* ── Five of this screen's reads live in `entities/client` now ────────────────────────────────
 *
 * The header, the note, the goal, the profile row and the calendar window moved there on
 * 31 August 2026, when the roster's KANON v2-D preview panel became their second consumer. They are
 * re-exported here so this slice's own call sites keep reading one file, and so that the two
 * surfaces cannot end up with two cache trees for one client — which is what two copies of a query
 * key always become. `clientDetailRoot` is the entity's now; everything below still descends from
 * it.
 */
/* The Ankiety tab's own read moved to `features/survey-report` on 17 September 2026, for the same
 * reason and with the same re-export: `/raporty`'s report dialog reads a client's survey history
 * too, and two query keys for one list would be two cache trees. */
export { clientSurveysQueryKey, clientSurveysQueryOptions } from '~/features/survey-report';

export {
  clientCalendarWindowQueryOptions,
  clientDetailQueryOptions,
  clientDetailRoot,
  clientGoalQueryKey,
  clientGoalQueryOptions,
  clientNoteQueryKey,
  clientNoteQueryOptions,
  clientProfileQueryOptions,
  BODY_HISTORY_LIMIT,
  clientBodyHistoryQueryKey,
  clientBodyHistoryQueryOptions,
} from '~/entities/client';
import { addDays, toIsoDate } from '~/shared/lib/calendarDate';

import { ATTENDANCE_WINDOW_DAYS } from '../model/attendance';
import { PROGRESS_WINDOW_DAYS, SESSION_TABLE_ROWS } from '../model/sessionTable';
import type { NutritionSpan } from '../model/nutrition';
import { isSelfPacedPlan, type DetailSource } from '../model/summaryColumn';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * The read the attendance card folds: this window and the one before it, in one request.
 *
 * `toIsoDate` writes the date in the BROWSER's zone, which is the zone the trainer reads dates in,
 * and the contract's `from`/`to` are `DateOnly` — a wall-clock date with no instant in it.
 * `toISOString().slice(0, 10)` is the trap it avoids: it converts to UTC first, so a trainer in
 * Warsaw opening the app before 02:00 asks for yesterday's window.
 */
export function attendanceRange(now: Date = new Date()): { from: string; to: string } {
  // Exactly the card's one window. It was double this while the card compared the last thirty
  // days against the thirty before them; the comparison went with the owner's 13.08 disposition,
  // and a read nothing folds is a month of rows nobody asked for.
  return {
    from: toIsoDate(addDays(now, -(ATTENDANCE_WINDOW_DAYS - 1))),
    to: toIsoDate(now),
  };
}

/**
 * **Keep the previous answer while a NEW RANGE loads, and only then.**
 *
 * Owner, 2 September 2026: "zmiana klienta albo zakresu dat → poprzednia treść zostaje, podmienia
 * się po dojściu nowej." For a range this is exactly what TanStack's `placeholderData` does, and it
 * is the honest case: the client is the same person, the chart on screen is the range they were
 * just looking at, and the range bar above it says which range that is.
 *
 * `keepPreviousData` on its own would ALSO hold across a change of CLIENT, because every key here
 * carries the client id — and a chart of one client's tonnage drawn under another client's name is
 * a lie, not a nicety. So the previous answer is kept only when the previous query described the
 * same client, which `clientDetailRoot`'s prefix is exactly the test for.
 *
 * The cost is stated: while the new range loads, the chart is the OLD range's, and a trainer who
 * reads it in that half-second reads the previous window. The range bar's own sentence names the
 * window, the card is `aria-busy` while it is true, and every alternative — a blank box, a
 * skeleton over a chart the trainer was already reading — was judged worse for a control the
 * trainer just operated.
 */
function keepAcrossRange<TData>(clientId: number): PlaceholderDataFunction<TData> {
  const root = clientDetailRoot(clientId);
  // `PlaceholderDataFunction` is the library's own shape, read off the installed typings: the
  // second argument is the previous `Query`, not a structural stand-in, and typing it any other
  // way makes `queryOptions` infer its data type from THIS function instead of from `queryFn`.
  return (previous, previousQuery) =>
    previousQuery !== undefined &&
    root.every((part, index) => previousQuery.queryKey[index] === part)
      ? previous
      : undefined;
}

/** The three range-keyed reads' response types, so `placeholderData` can be typed at its call site. */
type ConsistencyData = Awaited<ReturnType<typeof getCustomerIdConsistency>>;
type TrendsData = Awaited<ReturnType<typeof getCustomerIdTrainingTrends>>;
type MuscleVolumeData = Awaited<ReturnType<typeof getCustomerIdMuscleVolume>>;

export function clientConsistencyQueryOptions(clientId: number, range: { from: string; to: string }) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'consistency', range] as const,
    queryFn: ({ signal }) => getCustomerIdConsistency(clientId, range, undefined, signal),
    // Trendy's range bar re-keys this read. See `keepAcrossRange` — the grid the trainer was just
    // reading stays until the new window answers, and a change of CLIENT is never kept.
    placeholderData: keepAcrossRange<ConsistencyData>(clientId),
  });
}

/**
 * The compliance read, fetched for ONE field: `planStanding`.
 *
 * The card's header explains why the 30-day sentence comes from `/consistency` and not from here —
 * two endpoints answering one sentence is how two numbers learn to disagree. That rule is not
 * broken by this read, because the two never speak at the same time: `planStanding` is present
 * exactly when the client's plan is self-paced, and in that case the consistency fold is not
 * rendered at all. In every other case this read contributes nothing to the screen.
 *
 * It is needed because nothing cheaper carries the answer. `/consistency` has no self-paced
 * awareness whatsoever: it emits one verdict per calendar day, and an undated plan can produce no
 * missed day, so its fold would report a flawless 100% for exactly the client R15 exists to stop
 * scoring. `CustomerDetailResponse` carries `assignedPlan.schedulingMode`, which names the mode but
 * none of the R16 figures.
 *
 * The window is the contract's own default — the same thirty days the attendance card reads, so the
 * two cards describe the same span whichever one is showing.
 *
 * It fires only for a self-paced client. A dated one never renders a figure from here, so firing it
 * would buy nothing and cost a request on every client detail opened — the same rule the rest of
 * this file follows, where a tab that is not open asks for nothing.
 *
 * It takes the header SOURCE rather than a ready-made boolean, and that is the point: the gate must
 * be the plan's MODE, and handing it a flag would let a caller wire in a standing-derived one — the
 * exact confusion that produced the defect this read exists to fix, since the standing is populated
 * for an active assignment only and is null while this very query is in flight. Passing the source
 * makes `isSelfPacedPlan` the only possible answer, and makes anything else a type error.
 */
export function clientComplianceQueryOptions(clientId: number, detail: DetailSource | null) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'compliance'] as const,
    queryFn: ({ signal }) =>
      getCustomerIdCompliance(clientId, { window: ATTENDANCE_WINDOW_DAYS }, undefined, signal),
    // Not yet known reads as "do not fetch": the mode settles a moment later and the query starts
    // then, which is cheaper and more honest than firing for every client to find out.
    enabled: isSelfPacedPlan(detail),
  });
}

/**
 * The session table draws eight rows (KANON „Karty zadań", 24 September 2026; the timeline before
 * it drew six); the request asks for them rather than slicing a longer list.
 */
const RECENT_SESSION_COUNT = SESSION_TABLE_ROWS;

/** Not exported: nothing invalidates this read, so the key is only the options' own below. */
function clientRecentSessionsQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'recent-sessions'] as const;
}

export function clientRecentSessionsQueryOptions(clientId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...clientRecentSessionsQueryKey(clientId), lang] as const,
    queryFn: ({ signal }) =>
      getCustomerIdRecentSessions(clientId, { count: RECENT_SESSION_COUNT, lang }, undefined, signal),
  });
}

/**
 * How many sessions and records the last month held — the „Ostatnie sesje" heading's aside.
 *
 * `tz` for the same reason as the trends read below: the server counts the window's days, and a
 * window cut on the server's midnight rather than the trainer's is a different month.
 */
export function clientProgressSummaryQueryOptions(clientId: number) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'progress-summary', PROGRESS_WINDOW_DAYS, tz] as const,
    queryFn: ({ signal }) =>
      getCustomerIdProgressSummary(clientId, { days: PROGRESS_WINDOW_DAYS, tz }, undefined, signal),
  });
}

/**
 * Weekly tonnage and average sleep over a free date range.
 *
 * `tz` rides along because the SERVER buckets the weeks: without it a Monday-start week is cut on
 * the server's midnight rather than the trainer's, and a Sunday-evening session lands in the wrong
 * bar.
 *
 * Which is exactly why it sits at the tail of the key too: the bars belong to the zone they were
 * bucketed in, so a cached answer must not be handed back to a different one.
 */
export function clientTrendsQueryOptions(clientId: number, range: { from: string; to: string }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'training-trends', range, tz] as const,
    queryFn: ({ signal }) =>
      getCustomerIdTrainingTrends(clientId, { ...range, tz }, undefined, signal),
    placeholderData: keepAcrossRange<TrendsData>(clientId),
  });
}

/** Muscle volume over the same range the trends use — one range drives the whole tab (canon 07). */
export function clientMuscleVolumeQueryOptions(
  clientId: number,
  range: { from: string; to: string },
) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'muscle-volume', range] as const,
    queryFn: ({ signal }) => getCustomerIdMuscleVolume(clientId, range, undefined, signal),
    placeholderData: keepAcrossRange<MuscleVolumeData>(clientId),
  });
}

/** The 8-week window is the contract's default and the only one this surface asks for. */
const BODY_WINDOW_WEEKS = 8;

export function clientBodyQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'body'] as const;
}

export function clientBodyQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...clientBodyQueryKey(clientId), BODY_WINDOW_WEEKS] as const,
    queryFn: ({ signal }) =>
      getCustomerIdBody(clientId, { windowWeeks: BODY_WINDOW_WEEKS }, undefined, signal),
  });
}

/** The canon prints five rows in its PR feed and the request asks for a few more than that. */
const RECENT_PR_COUNT = 10;

export function clientRecordsQueryOptions(clientId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'personal-records', lang] as const,
    queryFn: ({ signal }) =>
      getCustomerIdPersonalRecords(clientId, { recentN: RECENT_PR_COUNT, lang }, undefined, signal),
  });
}

/** Both languages' copies of the movement list, which a seen mark or a reply makes stale. */
export function clientTrainedExercisesQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'trained-exercises'] as const;
}

/** Every movement the client has logged, with its clip recency and unread mark (R15). */
export function clientTrainedExercisesQueryOptions(clientId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...clientTrainedExercisesQueryKey(clientId), lang] as const,
    queryFn: ({ signal }) => getCustomerIdTrainedExercises(clientId, { lang }, undefined, signal),
  });
}

/** „Wszystkie ćwiczenia" — read only when that view opens. */
export function clientExerciseMatrixQueryOptions(clientId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'exercise-matrix', lang] as const,
    queryFn: ({ signal }) => getCustomerIdExerciseMatrix(clientId, { lang }, undefined, signal),
  });
}

/**
 * This client's self-booking exception (`GET /booking-policy/customer/{id}/self-booking`, R2) —
 * under the booking root, beside the general policy it is read against, so a write to either
 * reads both again.
 */
export function clientSelfBookingQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...BOOKING_ROOT, 'policy', 'self-booking', clientId] as const,
    queryFn: ({ signal }) =>
      getBookingPolicyCustomerCustomerIdSelfBooking(clientId, undefined, signal),
  });
}

/* ── Odżywianie ─────────────────────────────────────────────────────────────────────────────────
 * Three reads under one prefix, so the target sheet's save drops all of them at once: a new target
 * re-judges today, and today is in every range. A 404 is the module being off for this client
 * (`nutrition_unavailable`, R16) — an answer, so it is not retried. */

export function clientNutritionRoot(clientId: number) {
  return [...clientDetailRoot(clientId), 'nutrition'] as const;
}

const retryUnlessGone = (failures: number, error: unknown) => !isGone(error) && failures < 1;

export function clientNutritionTargetQueryKey(clientId: number) {
  return [...clientNutritionRoot(clientId), 'target'] as const;
}

export function clientNutritionTargetQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: clientNutritionTargetQueryKey(clientId),
    queryFn: ({ signal }) => getNutritionCustomersCustomerIdTarget(clientId, undefined, signal),
    retry: retryUnlessGone,
  });
}

/** The calculator's inputs (R19): read when the target sheet opens, never cached across visits. */
export function clientNutritionCalculatorQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...clientNutritionRoot(clientId), 'calculator-inputs'] as const,
    queryFn: ({ signal }) => getNutritionCustomersCustomerIdCalculatorInputs(clientId, undefined, signal),
    retry: retryUnlessGone,
    staleTime: 0,
    gcTime: 0,
  });
}

/** The answer with the span it answers, so a kept placeholder is drawn as ITS span, not the new one. */
export interface NutritionRangeData {
  readonly span: NutritionSpan;
  readonly dto: Awaited<ReturnType<typeof getNutritionCustomersCustomerIdRange>>;
}

const SPAN_PARAM = { week: 'Week', fourWeeks: 'FourWeeks', quarter: 'Quarter' } as const;

/** Page 0 only: the web draws the newest page of each span (R10's paging is not offered here). */
export function clientNutritionRangeQueryOptions(clientId: number, span: NutritionSpan) {
  return queryOptions({
    queryKey: [...clientNutritionRoot(clientId), 'range', span, 0] as const,
    queryFn: async ({ signal }): Promise<NutritionRangeData> => ({
      span,
      dto: await getNutritionCustomersCustomerIdRange(
        clientId,
        { span: SPAN_PARAM[span], page: 0 },
        undefined,
        signal,
      ),
    }),
    retry: retryUnlessGone,
    // The chart the trainer was reading stays until the new span answers; see `keepAcrossRange`.
    placeholderData: keepAcrossRange<NutritionRangeData>(clientId),
  });
}

/** One day's entries — read when its row in the week table is opened. */
export function clientNutritionDayQueryOptions(clientId: number, date: string) {
  return queryOptions({
    queryKey: [...clientNutritionRoot(clientId), 'day', date] as const,
    queryFn: ({ signal }) =>
      getNutritionCustomersCustomerIdDay(clientId, { date }, undefined, signal),
    retry: retryUnlessGone,
  });
}
